import { Input } from '@base-ui/react/input'
import clsx from 'clsx'
import { forwardRef, useMemo } from 'react'

// Light-weight US phone input. Backend (TextMessages::PhoneNumberFormatter)
// normalizes to E.164 server-side, so we deliberately don't ship
// libphonenumber-js in the client bundle for this POC.

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value: string
  onChange: (formatted: string, digits: string) => void
}

/** Returns only the digits in s (max 10). Strips a leading 1 if present and length is 11. */
export function phoneDigits(value: string): string {
  const raw = (value || '').replace(/\D/g, '')
  const stripped = raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw
  return stripped.slice(0, 10)
}

/** Formats a string of US phone digits as (XXX) XXX-XXXX. Partial input is supported. */
export function formatPhone(value: string): string {
  const digits = phoneDigits(value)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value, onChange, ...props }, ref) => {
    const formatted = useMemo(() => formatPhone(value), [value])

    return (
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(555) 123-4567"
        value={formatted}
        onChange={(e) => {
          const next = formatPhone(e.target.value)
          onChange(next, phoneDigits(e.target.value))
        }}
        className={clsx(
          'bg-white border border-gray-stroke h-10 w-full rounded-lg px-4 text-base outline-none transition-colors',
          'focus:ring-0 focus:border-lavender disabled:text-charcoal-50 disabled:cursor-not-allowed',
          'placeholder:text-charcoal-50',
          'data-invalid:border-brick-light data-invalid:focus:border-brick-light',
          className
        )}
        {...props}
      />
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
